import { Fragment, useMemo } from 'react';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import {
  HomePageLayoutBlueprint,
  HomePageWidgetBlueprint,
} from '@backstage/plugin-home-react/alpha';
import homePlugin from '@backstage/plugin-home/alpha';
import { CustomHomepageGrid } from '@backstage/plugin-home';
import { Content, Header, Page } from '@backstage/core-components';
import type {
  HomePageDefaultConfigItem,
  HomePageLayoutProps,
} from '@backstage/plugin-home-react/alpha';

// ── Default grid ─────────────────────────────────────────────────────────────
// What every user sees until they customise their own home page (the grid is
// 12 columns wide; rows are 60px). `component` is the widget's `name`.
// `page:home` `defaultConfig` in app-config, when set, replaces this.

const DEFAULT_HOME_GRID: HomePageDefaultConfigItem[] = [
  { component: 'HomePageSearchBar', column: 0, row: 0, width: 12, height: 2 },
  { component: 'HomePageToolkit', column: 0, row: 2, width: 6, height: 5 },
  { component: 'QuickLinks', column: 6, row: 2, width: 6, height: 5 },
  { component: 'HomePageMostVisited', column: 0, row: 7, width: 6, height: 6 },
  {
    component: 'HomePageRecentlyVisited',
    column: 6,
    row: 7,
    width: 6,
    height: 6,
  },
];

const GRID_COLUMNS = { xl: 12, lg: 12, md: 12, sm: 6, xs: 4, xxs: 2 };

// ── Layout ───────────────────────────────────────────────────────────────────

function ToolSenseHomePageLayout({
  widgets,
  defaultConfig = DEFAULT_HOME_GRID,
}: HomePageLayoutProps) {
  // Same mapping as the upstream DefaultHomePageLayout; memoised because the
  // grid regenerates widget ids whenever `config` changes identity.
  const config = useMemo(
    () =>
      defaultConfig.map(item => ({
        component: item.component,
        x: item.column,
        y: item.row,
        width: item.width,
        height: item.height,
        movable: item.movable,
        deletable: item.deletable,
        resizable: item.resizable,
      })),
    [defaultConfig],
  );

  return (
    <Page themeId="home">
      <Header
        title="Welcome to ToolSense IoT Backstage"
        subtitle="Your developer portal for the IoT platform"
      />
      <Content>
        {/* 12 columns from `md` up (upstream uses 10 at `md`), so the default
            half/half rows stay side by side on laptop-width screens. */}
        <CustomHomepageGrid config={config} cols={GRID_COLUMNS}>
          {widgets.map((widget, i) => (
            <Fragment key={widget.name ?? i}>{widget.component}</Fragment>
          ))}
        </CustomHomepageGrid>
      </Content>
    </Page>
  );
}

const homePageLayout = HomePageLayoutBlueprint.make({
  params: {
    loader: async () => ToolSenseHomePageLayout,
  },
});

// ── Quick Links widget ────────────────────────────────────────────────────────

const quickLinksWidget = HomePageWidgetBlueprint.make({
  name: 'quick-links',
  params: {
    name: 'QuickLinks',
    title: 'Quick Links',
    description: 'Quick access to key Backstage pages',
    layout: {
      width: { defaultColumns: 4, minColumns: 2, maxColumns: 6 },
      height: { defaultRows: 5, minRows: 4 },
    },
    components: () =>
      import('./components/home/QuickLinksWidget').then(m => ({
        Content: m.QuickLinksWidget,
      })),
  },
});

// ── Toolkit widget ────────────────────────────────────────────────────────────
// The team's main tools. Owned in code, so the deployed app gets them without
// manifest config; this replaces any `tools` set for the widget in app-config.
// `icon` names resolve through the app's icons API; unknown names fall back to
// a link icon.

const TOOLKIT_TOOLS = [
  {
    label: 'Grafana',
    url: 'https://grafana.iot.toolsense.io',
    icon: 'dashboard',
  },
  { label: 'Authentik', url: 'https://auth.iot.toolsense.io', icon: 'user' },
  { label: 'ERPNext', url: 'https://erpnext.toolsense.io' },
  { label: 'n8n', url: 'https://n8n.iot.toolsense.io' },
  { label: 'Docs', url: 'https://docs.iot.toolsense.io', icon: 'docs' },
  { label: 'GitHub', url: 'https://github.com/ToolSense', icon: 'github' },
  { label: 'Argo CD', url: 'https://argocd.iot.toolsense.io' },
];

const toolkitWidget = homePlugin
  .getExtension('home-page-widget:home/toolkit')
  .override({
    factory(originalFactory, { config }) {
      return originalFactory({ config: { ...config, tools: TOOLKIT_TOOLS } });
    },
  });

// ── Visit tracking ────────────────────────────────────────────────────────────
// Most Visited / Recently Visited need the visits API and the listener that
// records page views. The home plugin ships both disabled.

const visitsApi = homePlugin
  .getExtension('api:home/visits')
  .override({ disabled: false });

const visitListener = homePlugin
  .getExtension('app-root-element:home/visit-listener')
  .override({ disabled: false });

// ── Module ────────────────────────────────────────────────────────────────────

export const homeModule = createFrontendModule({
  pluginId: 'home',
  extensions: [
    homePageLayout,
    quickLinksWidget,
    toolkitWidget,
    visitsApi,
    visitListener,
  ],
});
